import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, View, Text, StyleSheet, TouchableOpacity, Dimensions, Platform, Easing, StatusBar, useWindowDimensions } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { NavigationContainer } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  Gesture,
  GestureDetector,
  type GestureType,
} from 'react-native-gesture-handler';

import LoginScreen from '../screens/LoginScreen';
import VerifyEmailScreen from '../screens/VerifyEmailScreen';
import FeedScreen from '../screens/FeedScreen';
import MapScreen from '../screens/MapScreen';
import DiscoverScreen from '../screens/DiscoverScreen';
import PlansScreen from '../screens/PlansScreen';
import { ChatListScreen, ChatDetailScreen } from '../screens/ChatScreen';
import ProfileScreen from '../screens/ProfileScreen';
import { PartnerAccountProfileScreen, PartnerAccountProfileTab, PublicIdentityRouterScreen } from '../screens/PartnerProfileRouterScreen';
import EventDetailScreen from '../screens/EventDetailScreen';
import VibeVenuesScreen from '../screens/VibeVenuesScreen';
import VenueProfileScreen from '../screens/VenueProfileScreen';
import OrganizerProfileScreen from '../screens/OrganizerProfileScreen';
import UploadEventScreen from '../screens/UploadEventScreen';
import UploadPlanScreen from '../screens/UploadPlanScreen';
import EditProfileScreen from '../screens/EditProfileScreen';
import SettingsScreen from '../screens/SettingsScreen';
import VerificationCenterScreen from '../screens/VerificationCenterScreen';
import MyTemplatesScreen from '../screens/MyTemplatesScreen';
import MyPostsScreen from '../screens/MyPostsScreen';
import PlanRequestsScreen from '../screens/PlanRequestsScreen';
import CompleteProfileScreen from '../screens/CompleteProfileScreen';
import DayVibesQuestionScreen from '../screens/DayVibesQuestionScreen';
import DayVibesAuraScreen from '../screens/DayVibesAuraScreen';
import VibesScreen from '../screens/VibesScreen';
import AllTopPicksScreen from '../screens/AllTopPicksScreen';
import PromotionScreen from '../screens/PromotionScreen';
import AdminPromotionsScreen from '../screens/AdminPromotionsScreen';
import AdminReviewReportsScreen from '../screens/AdminReviewReportsScreen';
import AdminPromotionEditorScreen from '../screens/AdminPromotionEditorScreen';
import ProfileSetupScreen from '../screens/ProfileSetupScreen';
import StudiZoneScreen from '../screens/StudiZoneScreen';
import StudiZoneMapScreen from '../screens/StudiZoneMapScreen';
import StudentVerificationCompleteScreen from '../screens/StudentVerificationCompleteScreen';
import ResetPasswordScreen from '../screens/ResetPasswordScreen';
import VerifyEmailCompleteScreen from '../screens/VerifyEmailCompleteScreen';
import PartnerSetPasswordScreen from '../screens/PartnerSetPasswordScreen';
import PartnerHomeScreen from '../screens/PartnerHomeScreen';
import PartnerAnalyticsScreen from '../screens/PartnerAnalyticsScreen';
import VenueAnalyticsScreen from '../screens/VenueAnalyticsScreen';
import EditVenueProfileScreen from '../screens/EditVenueProfileScreen';
import AddPartnerScreen from '../screens/AddPartnerScreen';
import FollowingPartnersScreen from '../screens/FollowingPartnersScreen';
import PartnerNotificationsScreen from '../screens/PartnerNotificationsScreen';
import CalendarImportsScreen from '../screens/CalendarImportsScreen';
import PartnerCalendarScreen from '../screens/PartnerCalendarScreen';
import {
  TermsOfServiceScreen,
  PrivacyPolicyScreen,
  ImpressumScreen,
  PartnerTermsScreen,
  CommunityGuidelinesScreen,
} from '../screens/LegalScreen';
import TonyteLoadingScreen from '../components/TonyteLoadingScreen';
import AppUpdatePrompt from '../components/AppUpdatePrompt';
import OptionalNudgeHost from '../components/OptionalNudgeHost';
import TonyteConfirmationHost from '../components/TonyteConfirmationHost';
import { nudgeSession } from '../utils/nudgeSession';
import { useChatUnreadSummary, useProfile } from '../hooks/useProfile';
import { usePartnerProfile } from '../hooks/usePartnerProfile';
import type { useAuth } from '../hooks/useAuth';
import { useChatNotifications } from '../hooks/useChatNotifications';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { useNotificationListeners } from '../hooks/useNotificationListeners';
import { navigationRef, markNavReady, markAuthReady, markMainReady, markVerificationReady, queueDeepLink, type DeepLinkTarget } from './notificationNav';
import { getInitialDeepLinkUrl, subscribeToDeepLinks } from './deepLinkTransport';

import { Colors, Spacing, Typography } from '../theme';
import type { MainTabParamList, MapFocusLocation, MessagesTabFocus, FeedFocus, UserRole } from '../types';
import { CityProvider } from '../context/CityContext';
import { DateProvider } from '../context/DateContext';
import { DEEP_LINK_PREFIXES } from '../utils/shareLinks';
import { useI18n } from '../i18n';
import { isPartner, normalizeUserRole } from '../utils/roles';
import { setSocialWriteRestricted } from '../utils/verificationGate';
import { ThemeProvider } from '../theme/ThemeProvider';
import { useTheme } from '../theme/useTheme';
import type { TonyteTheme } from '../theme/themes';
import { daylightStyle } from '../theme/daylightStyle';
import { Stack } from './createRootStack';
import { TabActiveContext } from './tabActivity';
import VibeClaimCoordinator from '../features/vibeClaim/VibeClaimCoordinator';
import { parseVibeClaimUrl } from '../features/vibeClaim/vibeClaimLinks';
import { savePendingVibeClaim } from '../features/vibeClaim/pendingVibeClaim';

// Web Vibe game claim links (go.tonyte.app/vibe/…) never go through
// react-navigation or the in-memory queueDeepLink gate: they are persisted
// straight away so they survive login, registration, onboarding, email
// verification and app restarts; VibeClaimCoordinator claims them once the
// account is fully ready. Returns true when the URL was one of them.
const captureVibeClaimLink = (url: string | null | undefined): boolean => {
  const credential = url ? parseVibeClaimUrl(url) : null;
  if (!credential) return false;
  console.log('[Tonyte][DeepLink] web vibe claim link captured');
  void savePendingVibeClaim(credential, 'link');
  return true;
};

// Visited main tabs stay mounted (hidden) — memo lets them skip re-rendering
// whenever MainTabs re-renders for a tab switch or badge change.
const MemoMapScreen = memo(MapScreen);
const MemoDiscoverScreen = memo(DiscoverScreen);
const MemoFeedScreen = memo(FeedScreen);
const MemoPlansScreen = memo(PlansScreen);
const MemoChatListScreen = memo(ChatListScreen);
const MemoProfileScreen = memo(ProfileScreen);

const normalizeStudentVerificationUrl = (url: string) => {
  try {
    const parsed = new URL(url);
    const tokenFromQuery = parsed.searchParams.get('token')?.trim() || '';
    const isTonyteScheme = parsed.protocol === 'tonyte:';
    const isNewVerifyPath = parsed.pathname === '/verify-student' || (isTonyteScheme && parsed.host === 'verify-student');
    const isOldVerifyPath =
      parsed.pathname === '/student-verification-complete' ||
      parsed.pathname === '/verify-student-email' ||
      (isTonyteScheme && parsed.host === 'student-verification-complete');
    if ((isNewVerifyPath || isOldVerifyPath) && tokenFromQuery) {
      if (isTonyteScheme) {
        return `tonyte://verify-student/${encodeURIComponent(tokenFromQuery)}`;
      }
      return `${parsed.origin}/verify-student/${encodeURIComponent(tokenFromQuery)}`;
    }
  } catch {
    const match = url.match(/^tonyte:\/\/student-verification-complete(?:\?token=([^&]+))?/);
    if (match?.[1]) return `tonyte://verify-student/${match[1]}`;
  }
  return url;
};

// Profile/event/venue/organizer/student-verification links (QR / share) are
// pulled out of React Navigation's own declarative URL handling and routed
// through the `queueDeepLink` gate instead (see notificationNav.ts). The
// declarative `linking.config` below only resolves against whatever screens
// are *currently* mounted — on a cold start that's the Login screen (or the
// profile-setup stack), neither of which registers most of these
// destinations, so a link tapped before the app is actually ready gets
// silently dropped. Queuing it ourselves survives until it's ready to open.
const extractPathId = (url: string, host: string, httpsPrefix: string): string | null => {
  try {
    const parsed = new URL(url);
    const isTonyteScheme = parsed.protocol === 'tonyte:';
    const rawId = isTonyteScheme && parsed.host === host
      ? parsed.pathname.replace(/^\/+/, '')
      : !isTonyteScheme && parsed.pathname.startsWith(httpsPrefix)
        ? parsed.pathname.slice(httpsPrefix.length)
        : '';
    const id = rawId.replace(/\/+$/, '');
    return id ? decodeURIComponent(id) : null;
  } catch {
    return null;
  }
};

const extractDeepLinkTarget = (url: string): DeepLinkTarget | null => {
  const userId = extractPathId(url, 'profile', '/profile/');
  if (userId) return { type: 'profile', userId };
  const eventId = extractPathId(url, 'event', '/event/');
  if (eventId) return { type: 'event', eventId };
  const venueId = extractPathId(url, 'venue', '/venue/');
  if (venueId) return { type: 'venue', venueId };
  const organizerId = extractPathId(url, 'organizer', '/organizer/');
  if (organizerId) return { type: 'organizer', organizerId };
  // Plans and events share the same detail screen (EventDetailScreen,
  // gated by entryType==='community_plan') and resolveEventActivityById()
  // already falls back to the `plans` collection for an id it can't find
  // among events — so a plan link reuses the exact same 'event' target
  // type rather than needing its own DeepLinkTarget/runDeepLink branch.
  const planId = extractPathId(url, 'plans', '/plans/');
  if (planId) return { type: 'event', eventId: planId };
  const chatId = extractPathId(url, 'chat', '/chat/');
  if (chatId) return { type: 'chat', chatId };
  const promotionId = extractPathId(url, 'promotion', '/promotion/');
  if (promotionId) return { type: 'promotion', promotionId };
  // `url` here has already been through normalizeStudentVerificationUrl()
  // (both call sites below apply it before calling this function), so every
  // supported format — the canonical path, the legacy query-param aliases,
  // and the tonyte:// scheme — has already converged on the one canonical
  // `.../verify-student/{token}` shape; no separate token parsing needed.
  const verificationToken = extractPathId(url, 'verify-student', '/verify-student/');
  if (verificationToken) return { type: 'studentVerification', token: verificationToken };
  return null;
};

const LINKING_CONFIG = {
  prefixes: DEEP_LINK_PREFIXES,
  config: {
    screens: {
      EventDetail: {
        path: 'event/:eventId',
      },
      VenueProfile: {
        path: 'venue/:venueId',
      },
      OrganizerProfile: {
        path: 'organizer/:organizerId',
      },
      PublicProfile: {
        path: 'profile/:userId',
      },
      // Registered so react-navigation's own web `useLinking` resolution
      // (which runs once at NavigationContainer mount, independent of the
      // queueDeepLink pipeline below) recognizes this as a known path
      // pattern instead of falling back away from it — reproduced live:
      // without this entry, refreshing on a valid /chat/:chatId destination
      // bounced to Main even though the initial cold load resolved
      // correctly, matching the exact class of bug this file's other
      // comments already document for the four routes above.
      ChatDetail: {
        path: 'chat/:chatId',
      },
      // Same reason as ChatDetail above — lets a refreshed /promotion/:id web
      // URL resolve instead of bouncing to Main.
      Promotion: {
        path: 'promotion/:promotionId',
      },
      StudentVerificationComplete: {
        path: 'verify-student/:token',
      },
      ResetPassword: {
        path: 'reset-password',
      },
      VerifyEmailComplete: {
        // Kept in sync with the real web page's path (Tonyte_website's
        // /auth/verify-email) as a redundant fallback in case a browser
        // ever hands this URL to the app directly — it is NOT in the
        // Android App Links pathPrefix allowlist or the iOS AASA `paths`
        // list, so the OS never actually routes here on its own; the web
        // page is the primary, always-reachable path (see
        // functions/index.js's requestEmailVerificationEmailHandler).
        path: 'auth/verify-email',
      },
    },
  },
  async getInitialURL() {
    // getInitialDeepLinkUrl has a native implementation (RN's Linking
    // module) and a .web.ts counterpart (window.location.href) — see
    // deepLinkTransport.ts. Guarded so a platform-specific quirk here can
    // never leave NavigationContainer stuck waiting on a rejected promise.
    try {
      const url = await getInitialDeepLinkUrl();
      if (captureVibeClaimLink(url)) return null;
      const normalizedUrl = url ? normalizeStudentVerificationUrl(url) : url;
      if (normalizedUrl) console.log('[Tonyte][DeepLink] initial url', normalizedUrl);
      const target = normalizedUrl ? extractDeepLinkTarget(normalizedUrl) : null;
      if (target) {
        queueDeepLink(target);
        return null;
      }
      return normalizedUrl;
    } catch (error) {
      if (__DEV__) console.warn('[Tonyte][DeepLink] getInitialURL unavailable', error);
      return null;
    }
  },
  subscribe(listener: (url: string) => void) {
    try {
      return subscribeToDeepLinks(url => {
        if (captureVibeClaimLink(url)) return;
        const normalizedUrl = normalizeStudentVerificationUrl(url);
        console.log('[Tonyte][DeepLink] runtime url', normalizedUrl);
        const target = extractDeepLinkTarget(normalizedUrl);
        if (target) {
          queueDeepLink(target);
          return;
        }
        listener(normalizedUrl);
      });
    } catch (error) {
      if (__DEV__) console.warn('[Tonyte][DeepLink] subscribe unavailable', error);
      return () => {};
    }
  },
};
const { width } = Dimensions.get('window');
const NAV_WIDTH = Math.min(width, 450);
const NAV_HEIGHT = 360;
const NAV_RADIUS = Math.min(210, NAV_WIDTH * 0.466);
const ICON_RADIUS = Math.min(142, NAV_WIDTH * 0.315);
const HOVER_THRESHOLD = 84;
const LOCK_THRESHOLD = 50;
const CENTER_DRAG_THRESHOLD = 10;
const MAIN_TAB_ORDER: Array<keyof MainTabParamList> = ['Map', 'Discover', 'Feed', 'Plans', 'Chat', 'Profile'];
// Unverified email/password 'member' accounts may only browse Events (Feed)
// and Map — Discover/Vibe, Plans and Chat are entirely social/write surfaces
// (see the audit in the email-verification task) and are hidden from the tab
// bar entirely rather than gated per-action.
const RESTRICTED_ACCESS_TABS: Array<keyof MainTabParamList> = ['Map', 'Feed', 'Profile'];
const NAV_ONBOARDING_TAP_LIMIT = 3;
const DEBUG_NAVIGATION_GATE = false;

const CENTER_SPARKS = [
  { size: 5, radius: 52, angle: 12, duration: 10500, opacity: 0.72 },
  { size: 3, radius: 62, angle: 126, duration: 12800, opacity: 0.58 },
  { size: 4, radius: 46, angle: 218, duration: 11600, opacity: 0.66 },
  { size: 3, radius: 70, angle: 294, duration: 14200, opacity: 0.48 },
];

// ─── Tab Bar ─────────────────────────────────────────────────────────────────

// Three rounded rectangles offset diagonally — reads instantly as "stacked content cards"
function StackedCardsIcon({ color }: { color: string }) {
  const CARD_W = 15;
  const CARD_H = 10;
  const STEP = 3;
  const cards = [
    { left: STEP * 2, top: 0, opacity: 0.36 },
    { left: STEP,     top: STEP, opacity: 0.65 },
    { left: 0,        top: STEP * 2, opacity: 1 },
  ];
  return (
    <View style={{ width: 21, height: 21 }}>
      {cards.map((card, i) => (
        <View
          key={i}
          style={{
            position: 'absolute',
            left: card.left,
            top: card.top,
            width: CARD_W,
            height: CARD_H,
            borderRadius: 2.5,
            borderWidth: i === 2 ? 1.6 : 1.2,
            borderColor: color,
            opacity: card.opacity,
            backgroundColor: 'transparent',
          }}
        />
      ))}
    </View>
  );
}

const TAB_META: Record<string, { icon: string; label: string; angle: number }> = {
  Map: { icon: 'map', label: 'Map', angle: 160 },
  Discover: { icon: 'search', label: 'Vibes', angle: 132 },
  Feed: { icon: 'layout', label: 'Feed', angle: 104 },
  Plans: { icon: 'calendar', label: 'Plans', angle: 76 },
  Chat: { icon: 'message-circle', label: 'Chat', angle: 48 },
  Profile: { icon: 'user', label: 'Profile', angle: 20 },
};

const TAB_CUSTOM_ICONS: Partial<Record<string, (color: string) => React.ReactNode>> = {
  Feed: (color) => <StackedCardsIcon color={color} />,
};

function CustomTabBar({
  state,
  navigation,
  onNavigateTab,
  hasUnreadMessages = false,
  unreadChatCount = 0,
  navbarTapCount = 0,
  onCenterButtonTap,
  allowedTabs,
}: any) {
  const visibleTabMeta = useMemo(() => {
    if (!allowedTabs) return TAB_META;
    return Object.fromEntries(Object.entries(TAB_META).filter(([name]) => allowedTabs.includes(name)));
  }, [allowedTabs]);
  const insets = useSafeAreaInsets();
  const { t } = useI18n();
  const { theme } = useTheme();
  const themedNavStyles = useMemo(() => createNavThemeStyles(theme), [theme]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [gestureItem, setGestureItem] = useState<string | null>(null);
  const [lockedItem, setLockedItem] = useState<string | null>(null);
  const [pulsingItem, setPulsingItem] = useState<string | null>(null);
  const [expandedItemsMounted, setExpandedItemsMounted] = useState(false);
  const expandProgress = useRef(new Animated.Value(0)).current;
  const centerBreathProgress = useRef(new Animated.Value(0)).current;
  const centerRingProgress = useRef(new Animated.Value(0)).current;
  const centerSparkProgress = useRef(new Animated.Value(0)).current;
  const centerGlowIntensity = useRef(new Animated.Value(1)).current;
  const lockedItemRef = useRef<string | null>(null);
  const quickDragStartedRef = useRef(false);
  const wasExpandedRef = useRef(false);
  const isExpandedRef = useRef(false);
  const centerDragStartRef = useRef({ x: 0, y: 0 });
  const cx = NAV_WIDTH / 2;
  const cy = NAV_HEIGHT;
  const activeRouteName = state.routes[state.index]?.name;
  const centerGlowActive = navbarTapCount < NAV_ONBOARDING_TAP_LIMIT;

  useEffect(() => {
    const nextIntensity = navbarTapCount <= 0 ? 1 : navbarTapCount === 1 ? 0.7 : navbarTapCount === 2 ? 0.35 : 0;
    Animated.timing(centerGlowIntensity, {
      toValue: nextIntensity,
      duration: navbarTapCount >= NAV_ONBOARDING_TAP_LIMIT ? 520 : 360,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [centerGlowIntensity, navbarTapCount]);

  useEffect(() => {
    if (!centerGlowActive) {
      centerBreathProgress.stopAnimation();
      centerRingProgress.stopAnimation();
      centerSparkProgress.stopAnimation();
      return undefined;
    }

    const breath = Animated.loop(
      Animated.sequence([
        Animated.timing(centerBreathProgress, {
          toValue: 1,
          duration: 1250,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
        Animated.timing(centerBreathProgress, {
          toValue: 0,
          duration: 1250,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
      ]),
    );
    const ring = Animated.loop(
      Animated.sequence([
        Animated.timing(centerRingProgress, {
          toValue: 1,
          duration: 2600,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.delay(400),
        Animated.timing(centerRingProgress, {
          toValue: 0,
          duration: 0,
          useNativeDriver: true,
        }),
      ]),
    );
    const sparks = Animated.loop(
      Animated.timing(centerSparkProgress, {
        toValue: 1,
        duration: 14000,
        easing: Easing.linear,
        useNativeDriver: true,
      }),
    );

    centerBreathProgress.setValue(0);
    centerRingProgress.setValue(0);
    centerSparkProgress.setValue(0);
    breath.start();
    ring.start();
    sparks.start();
    return () => {
      breath.stop();
      ring.stop();
      sparks.stop();
    };
  }, [centerBreathProgress, centerGlowActive, centerRingProgress, centerSparkProgress]);

  useEffect(() => {
    isExpandedRef.current = isExpanded;
    Animated.spring(expandProgress, {
      toValue: isExpanded ? 1 : 0,
      stiffness: 340,
      damping: 30,
      mass: 1,
      useNativeDriver: true,
    }).start();
  }, [expandProgress, isExpanded]);

  useEffect(() => {
    if (theme.isDark) {
      setExpandedItemsMounted(isExpanded);
      return undefined;
    }
    if (isExpanded) {
      setExpandedItemsMounted(true);
      return undefined;
    }
    const unmountTimer = setTimeout(() => setExpandedItemsMounted(false), 300);
    return () => clearTimeout(unmountTimer);
  }, [isExpanded, theme.isDark]);

  const bgScaleX = expandProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [0.52, 1],
  });
  const bgScaleY = expandProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [0.38, 1],
  });
  const bgOpacity = expandProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [0.85, 1],
  });

  const upperGlassOpacity = expandProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [0.52, 0.92],
  });
  const expandedOpacity = expandProgress.interpolate({
    inputRange: [0, 0.45, 1],
    outputRange: [0, 0, 1],
  });
  const centerAuraScale = centerBreathProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 1.18],
  });
  const centerAuraOpacity = Animated.multiply(
    centerGlowIntensity,
    centerBreathProgress.interpolate({
      inputRange: [0, 0.52, 1],
      outputRange: [0.38, 0.58, 0.34],
    }),
  );
  const centerSpotlightOpacity = Animated.multiply(
    centerGlowIntensity,
    centerBreathProgress.interpolate({
      inputRange: [0, 1],
      outputRange: [0.12, 0.2],
    }),
  );
  const centerRingScale = centerRingProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [0.82, 1.84],
  });
  const centerRingOpacity = Animated.multiply(
    centerGlowIntensity,
    centerRingProgress.interpolate({
      inputRange: [0, 0.12, 0.72, 1],
      outputRange: [0, 0.46, 0.16, 0],
    }),
  );
  const centerButtonLift = centerGlowIntensity.interpolate({
    inputRange: [0, 1],
    outputRange: [0, -5],
  });
  const centerButtonScale = centerGlowIntensity.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 1.045],
  });

  const findClosestItem = useCallback((x: number, y: number, threshold: number) => {
    let closest: string | null = null;
    let minDist = threshold;

    Object.entries(visibleTabMeta).forEach(([routeName, meta]: [string, any]) => {
      const rad = (meta.angle * Math.PI) / 180;
      const ix = cx + ICON_RADIUS * Math.cos(rad);
      const iy = cy - ICON_RADIUS * Math.sin(rad);
      const dist = Math.hypot(x - ix, y - iy);
      if (dist < minDist) {
        minDist = dist;
        closest = routeName;
      }
    });

    return closest;
  }, [cx, cy, visibleTabMeta]);

  const updateDragSelection = useCallback((x: number, y: number) => {
    const hover = findClosestItem(x, y, HOVER_THRESHOLD);
    const lock = findClosestItem(x, y, LOCK_THRESHOLD);
    lockedItemRef.current = lock;
    setGestureItem(hover);
    setLockedItem(lock);
  }, [findClosestItem]);

  const toggleCenterExpansion = useCallback(() => {
    onCenterButtonTap?.();
    setIsExpanded(open => {
      return !open;
    });
  }, [onCenterButtonTap]);

  const finishCompassDrag = useCallback(() => {
    const selected = lockedItemRef.current;
    const startedExpanded = wasExpandedRef.current;
    setIsDragging(false);
    setGestureItem(null);
    setLockedItem(null);
    lockedItemRef.current = null;
    quickDragStartedRef.current = false;
    wasExpandedRef.current = false;

    if (!selected) {
      if (!startedExpanded) {
        setIsExpanded(false);
      }
      return;
    }
    setPulsingItem(selected);
    setTimeout(() => {
      onNavigateTab(selected);
      setPulsingItem(null);
      setIsExpanded(false);
    }, 200);
  }, [onNavigateTab]);

  const cancelCompassDrag = useCallback(() => {
    setIsDragging(false);
    setGestureItem(null);
    setLockedItem(null);
    lockedItemRef.current = null;
    quickDragStartedRef.current = false;
    wasExpandedRef.current = false;
  }, []);

  const dimTapGesture = useMemo(() =>
    Gesture.Tap().runOnJS(true).maxDuration(500).onEnd(() => setIsExpanded(false)),
  []);

  // Stable gesture — no isExpanded in deps. Button geometry is read from refs at start time
  // so RNGH never swaps the recognizer mid-drag when expansion state changes.
  const centerDragGesture = useMemo(() => {
    return Gesture.Pan()
      .runOnJS(true)
      .minDistance(CENTER_DRAG_THRESHOLD)
      .onStart(event => {
        const expanded = isExpandedRef.current;
        const buttonSize = expanded ? 82 : 58;
        const buttonLeft = cx - buttonSize / 2;
        const buttonTop = NAV_HEIGHT + insets.bottom - Math.max(insets.bottom, 4) - buttonSize;
        const startX = buttonLeft + event.x;
        const startY = buttonTop + event.y;
        centerDragStartRef.current = { x: startX, y: startY };
        quickDragStartedRef.current = true;
        wasExpandedRef.current = expanded;
        setIsDragging(true);
        setIsExpanded(true);
        updateDragSelection(startX, startY);
      })
      .onUpdate(event => {
        if (!quickDragStartedRef.current) return;
        const x = centerDragStartRef.current.x + event.translationX;
        const y = centerDragStartRef.current.y + event.translationY;
        updateDragSelection(x, y);
      })
      .onEnd(() => {
        if (quickDragStartedRef.current) {
          finishCompassDrag();
        }
      })
      .onFinalize(() => {
        if (quickDragStartedRef.current) {
          cancelCompassDrag();
        }
      });
  // cx and insets.bottom are the only layout values that genuinely change the geometry
  }, [cancelCompassDrag, cx, finishCompassDrag, insets.bottom, updateDragSelection]);

  return (
    <>
      {isExpanded ? (
        <GestureDetector gesture={dimTapGesture}>
          <View style={[styles.navDim, themedNavStyles.navDim]} />
        </GestureDetector>
      ) : null}

      <View
        pointerEvents="box-none"
        style={[
          styles.tabRoot,
          {
            height: NAV_HEIGHT + insets.bottom,
            paddingBottom: insets.bottom,
            width: NAV_WIDTH,
            marginLeft: -NAV_WIDTH / 2,
            bottom: 0,
          },
        ]}
      >
        <View style={styles.navClip} pointerEvents="none">
          <Animated.View
            style={[
              styles.navCircleWrap,
              themedNavStyles.navCircleWrap,
              {
                width: NAV_RADIUS * 2,
                height: NAV_RADIUS * 2,
                borderRadius: NAV_RADIUS,
                left: cx - NAV_RADIUS,
                top: cy - NAV_RADIUS,
                opacity: bgOpacity,
                transform: [{ scaleX: bgScaleX }, { scaleY: bgScaleY }],
              },
            ]}
          >
            <LinearGradient
              colors={theme.gradients.navBase}
              locations={[0, 0.48, 1]}
              start={{ x: 0.5, y: 0 }}
              end={{ x: 0.5, y: 1 }}
              style={[styles.navCircle, { borderRadius: NAV_RADIUS }]}
            />
            <LinearGradient
              colors={theme.gradients.navAccent}
              locations={[0, 0.34, 0.72]}
              start={{ x: 0.5, y: 0 }}
              end={{ x: 0.5, y: 1 }}
              style={[styles.navEdgeTint, { borderRadius: NAV_RADIUS }]}
            />
            <LinearGradient
              colors={theme.gradients.navGlass}
              locations={[0, 0.36, 1]}
              start={{ x: 0.22, y: 0 }}
              end={{ x: 0.78, y: 0.7 }}
              style={[styles.navGlassSurface, { borderRadius: NAV_RADIUS }]}
            />
            <LinearGradient
              colors={theme.gradients.navWarm}
              locations={[0.12, 0.72, 1]}
              start={{ x: 0.5, y: 0 }}
              end={{ x: 0.5, y: 1 }}
              style={[styles.navWarmBlend, { borderRadius: NAV_RADIUS }]}
            />
            <Animated.View
              style={[
                styles.navUpperGlass,
                themedNavStyles.navUpperGlass,
                {
                  opacity: upperGlassOpacity,
                  borderRadius: NAV_RADIUS,
                },
              ]}
            />
            <View style={[styles.navInnerShade, themedNavStyles.navInnerShade]} />
          </Animated.View>

          <Animated.View style={{ opacity: expandedOpacity }}>
            <View style={[styles.dialGuideOuter, themedNavStyles.dialGuideOuter, { left: cx - 164, top: cy - 164 }]} />
            <View style={[styles.arcLine, themedNavStyles.arcLine, { left: cx - 105, top: cy - 105 }]} />
            {Array.from({ length: 38 }).map((_, tick) => {
              const deg = 16 + tick * 4;
              const rad = (deg * Math.PI) / 180;
              const tickRoute = Object.entries(visibleTabMeta).find(([, meta]: [string, any]) => meta.angle === deg)?.[0] || null;
              const isMajor = Boolean(tickRoute);
              const isHighlighted = tickRoute === lockedItem || (!isDragging && tickRoute === activeRouteName);
              const dialRoute = lockedItem || gestureItem || pulsingItem || activeRouteName;
              const selectedAngle = visibleTabMeta[dialRoute]?.angle ?? TAB_META.Map.angle;
              const selectedDistance = Math.abs(deg - selectedAngle);
              const isSelectedSegment = selectedDistance <= 12;
              const isSelectedCore = selectedDistance <= 4;
              const length = isMajor ? 22 : deg % 12 === 0 ? 14 : 8;
              const outer = NAV_RADIUS - 4;
              return (
                <View
                  key={deg}
                  style={[
                    styles.tick,
                    themedNavStyles.tick,
                    isSelectedSegment && themedNavStyles.tickSelectedSegment,
                    isHighlighted && styles.tickHighlighted,
                    isSelectedCore && themedNavStyles.tickSelectedCore,
                    {
                      width: isMajor ? 2 : 1,
                      height: isHighlighted ? length + 4 : length,
                      opacity: isHighlighted ? 0.96 : isSelectedCore ? 0.84 : isSelectedSegment ? 0.46 : isMajor ? 0.34 : deg % 12 === 0 ? 0.17 : 0.08,
                      left: cx + outer * Math.cos(rad),
                      top: cy - outer * Math.sin(rad),
                      transform: [{ rotate: `${90 - deg}deg` }],
                    },
                  ]}
                />
              );
            })}
          </Animated.View>
        </View>

        {expandedItemsMounted && state.routes.map((route: any, index: number) => {
          const meta = TAB_META[route.name] ?? TAB_META.Feed;
          const label =
            route.name === 'Map' ? t('nav.map') :
            route.name === 'Discover' ? t('nav.discover') :
            route.name === 'Feed' ? t('nav.feed') :
            route.name === 'Plans' ? t('nav.plans') :
            route.name === 'Chat' ? t('nav.messages') :
            route.name === 'Profile' ? t('nav.profile') :
            meta.label;
          const isFocused = state.index === index;
          const isHovered = gestureItem === route.name && !lockedItem;
          const isLocked = lockedItem === route.name;
          const isPulsing = pulsingItem === route.name;
          const rad = (meta.angle * Math.PI) / 180;
          const x = cx + ICON_RADIUS * Math.cos(rad) - 27;
          const y = cy - ICON_RADIUS * Math.sin(rad) - 32;
          const settleStart = Math.min(0.68, 0.26 + index * 0.045);
          const itemSettle = expandProgress.interpolate({
            inputRange: [0, settleStart, 1],
            outputRange: [0, 0, 1],
            extrapolate: 'clamp',
          });
          const itemLift = itemSettle.interpolate({ inputRange: [0, 1], outputRange: [12, 0] });
          const itemScale = itemSettle.interpolate({ inputRange: [0, 1], outputRange: [0.94, 1] });

          const onPress = () => {
            const event = navigation?.emit
              ? navigation.emit({ type: 'tabPress', target: route.key, canPreventDefault: true })
              : { defaultPrevented: false };
            const finishSelection = () => {
              if (!isFocused && !event.defaultPrevented) {
                onNavigateTab(route.name);
              }
              setPulsingItem(null);
              setIsExpanded(false);
            };
            if (theme.isDark) {
              finishSelection();
              return;
            }
            setPulsingItem(route.name);
            setTimeout(finishSelection, 210);
          };

          return (
            <Animated.View
              key={route.key}
              style={[
                styles.compassItem,
                {
                  left: x,
                  top: y,
                  opacity: theme.isDark ? expandedOpacity : itemSettle,
                  transform: theme.isDark
                    ? [{ scale: expandProgress }]
                    : [{ translateY: itemLift }, { scale: itemScale }],
                },
              ]}
              pointerEvents={isExpanded ? 'auto' : 'none'}
            >
              <TouchableOpacity onPress={onPress} activeOpacity={0.7} style={styles.compassHit}>
                <View
                  style={[
                    styles.compassButton,
                    themedNavStyles.compassButton,
                    isFocused && styles.compassButtonActive,
                    isFocused && themedNavStyles.compassButtonActive,
                    isHovered && styles.compassButtonHovered,
                    isLocked && styles.compassButtonLocked,
                    isPulsing && styles.compassButtonPulsing,
                  ]}
                >
                  <LinearGradient
                    colors={
                      isFocused ? theme.gradients.navButtonActive : theme.gradients.navButtonInactive
                    }
                    locations={isFocused ? [0, 0.48, 1] : [0, 0.52, 1]}
                    start={{ x: 0.35, y: 0 }}
                    end={{ x: 0.65, y: 1 }}
                    style={[styles.compassButtonFill, themedNavStyles.compassButtonFill]}
                  >
                    <View pointerEvents="none" style={[styles.compassButtonReflection, themedNavStyles.compassButtonReflection]} />
                    {TAB_CUSTOM_ICONS[route.name]
                      ? TAB_CUSTOM_ICONS[route.name]!(isFocused ? theme.colors.navIconActive : theme.colors.navIcon)
                      : <Icon name={meta.icon} size={21} color={isFocused ? theme.colors.navIconActive : theme.colors.navIcon} />}
                    {route.name === 'Chat' && unreadChatCount > 0 ? (
                      <View style={styles.unreadCountBadge}>
                        <Text style={styles.unreadCountText}>{unreadChatCount > 99 ? '99+' : unreadChatCount}</Text>
                      </View>
                    ) : route.name === 'Chat' && hasUnreadMessages ? (
                      <View style={styles.unreadDot} />
                    ) : null}
                  </LinearGradient>
                </View>
                <Text style={[styles.compassLabel, themedNavStyles.compassLabel, isFocused && styles.compassLabelActive, isFocused && themedNavStyles.compassLabelActive]}>
                  {label}
                </Text>
              </TouchableOpacity>
            </Animated.View>
          );
        })}

        {centerGlowActive ? (
          <View pointerEvents="none" style={StyleSheet.absoluteFill}>
            <Animated.View
              style={[
                styles.centerSpotlight,
                {
                  left: cx - 115,
                  bottom: Math.max(insets.bottom, 4) - 88,
                  opacity: isExpanded ? 0.06 : centerSpotlightOpacity,
                  transform: [{ scale: centerAuraScale }],
                },
              ]}
            />
            <Animated.View
              style={[
                styles.centerAura,
                {
                  left: cx - 96,
                  bottom: Math.max(insets.bottom, 4) - 66,
                  opacity: isExpanded ? 0.08 : centerAuraOpacity,
                  transform: [{ scale: centerAuraScale }],
                },
              ]}
            >
              <LinearGradient
                colors={['rgba(255,180,86,0.22)', 'rgba(242,125,38,0.105)', 'rgba(242,125,38,0)']}
                locations={[0, 0.46, 1]}
                style={styles.centerAuraFill}
              />
            </Animated.View>
            <Animated.View
              style={[
                styles.centerOuterRing,
                {
                  left: cx - 59,
                  bottom: Math.max(insets.bottom, 4) - 30,
                  opacity: isExpanded ? 0 : centerRingOpacity,
                  transform: [{ scale: centerRingScale }],
                },
              ]}
            />
            <View
              style={[
                styles.centerSparkOrbit,
                {
                  left: cx - 80,
                  bottom: Math.max(insets.bottom, 4) - 51,
                },
              ]}
            >
              {CENTER_SPARKS.map((spark, index) => {
                const spin = centerSparkProgress.interpolate({
                  inputRange: [0, 1],
                  outputRange: [`${spark.angle}deg`, `${spark.angle + 360}deg`],
                });
                const float = centerBreathProgress.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0, index % 2 === 0 ? -5 : 5],
                });
                return (
                  <Animated.View
                    key={spark.angle}
                    style={[
                      styles.centerSparkTrack,
                      {
                        opacity: isExpanded ? 0 : Animated.multiply(centerGlowIntensity, spark.opacity),
                        transform: [{ rotate: spin }],
                      },
                    ]}
                  >
                    <Animated.View
                      style={[
                        styles.centerSpark,
                        {
                          width: spark.size,
                          height: spark.size,
                          borderRadius: spark.size / 2,
                          transform: [{ translateY: -spark.radius }, { translateX: float }],
                        },
                      ]}
                    />
                  </Animated.View>
                );
              })}
            </View>
          </View>
        ) : null}

        <GestureDetector gesture={centerDragGesture}>
          <Animated.View
            style={[
              styles.centerButtonShell,
              {
                left: cx - (isExpanded ? 41 : 29),
                bottom: Math.max(insets.bottom, 4),
                transform: [{ translateY: centerGlowActive ? centerButtonLift : 0 }, { scale: centerGlowActive ? centerButtonScale : 1 }],
              },
            ]}
          >
            <TouchableOpacity
              activeOpacity={0.82}
              onPress={toggleCenterExpansion}
              onLongPress={() => {
                setIsExpanded(true);
              }}
              delayLongPress={130}
              style={[
                styles.centerButton,
                themedNavStyles.centerButton,
                isExpanded && styles.centerButtonOpen,
                centerGlowActive && !isExpanded && styles.centerButtonOnboarding,
              ]}
            >
              <LinearGradient
                colors={
                  isExpanded
                    ? ['rgba(24,32,58,0.98)', 'rgba(7,10,19,0.99)']
                    : theme.gradients.accent
                }
                style={[
                  styles.centerButtonFill,
                  isExpanded && styles.centerButtonFillOpen,
                ]}
              >
                <Icon name={isExpanded ? 'x' : 'plus'} size={22} color="#fff" />
              </LinearGradient>
            </TouchableOpacity>
          </Animated.View>
        </GestureDetector>
      </View>
    </>
  );
}

// ─── Main Tabs ────────────────────────────────────────────────────────────────

interface MainTabsProps {
  userId: string | null;
  onSignOut: () => void;
  initialTab?: keyof MainTabParamList;
  role?: UserRole;
  focusMapLocation?: MapFocusLocation;
  focusMessagesTab?: MessagesTabFocus;
  focusFeed?: FeedFocus;
  restrictedAccess?: boolean;
  onOpenVerifyEmail?: () => void;
}

function MainTabs({ userId, onSignOut, initialTab, role, focusMapLocation, focusMessagesTab, focusFeed, restrictedAccess = false, onOpenVerifyEmail }: MainTabsProps) {
  const { theme, themeName } = useTheme();
  const { t } = useI18n();
  const insets = useSafeAreaInsets();
  const { width: viewportWidth } = useWindowDimensions();
  const isDesktopWeb = Platform.OS === 'web' && viewportWidth >= 1024;
  const mainTabsThemeStyles = useMemo(() => createMainTabsThemeStyles(theme), [theme]);
  const tabOrder = useMemo(
    () => (restrictedAccess ? MAIN_TAB_ORDER.filter(name => RESTRICTED_ACCESS_TABS.includes(name)) : MAIN_TAB_ORDER),
    [restrictedAccess],
  );
  // A normal cold start should land partner accounts on their primary
  // identity. Explicit destinations (deep links, notifications and calls to
  // Main with initialTab) still take precedence.
  const requestedInitialTab: keyof MainTabParamList = initialTab ?? (isPartner(role) ? 'Profile' : 'Map');
  const safeInitialTab = tabOrder.includes(requestedInitialTab) ? requestedInitialTab : 'Map';
  const [activeTabName, setActiveTabName] = useState<keyof MainTabParamList>(safeInitialTab);
  // Tabs mount lazily on first visit, then stay mounted for the rest of the
  // session (hidden via `display: none`, not unmounted) — switching tabs
  // must reuse existing screen state (scroll position, map camera, loaded
  // images, in-flight hooks) instead of tearing everything down and
  // rebuilding it from scratch on every switch.
  const [mountedTabs, setMountedTabs] = useState<Set<keyof MainTabParamList>>(() => new Set([safeInitialTab]));
  useEffect(() => {
    setMountedTabs(prev => (prev.has(activeTabName) ? prev : new Set(prev).add(activeTabName)));
  }, [activeTabName]);
  // If access becomes restricted mid-session (shouldn't normally happen —
  // emailVerified only ever becomes true, never false again — but defends
  // against landing on a now-hidden tab) or the active tab is no longer in
  // tabOrder, fall back to Map.
  useEffect(() => {
    if (!tabOrder.includes(activeTabName)) {
      setActiveTabName('Map');
    }
  }, [activeTabName, tabOrder]);
  const [imagePreviewActive, setImagePreviewActive] = useState(false);
  const [navbarTapCount, setNavbarTapCount] = useState(0);
  const activeIndex = tabOrder.indexOf(activeTabName);
  const activeIndexRef = useRef(activeIndex);
  const feedScrollGestureRef = useRef<GestureType | undefined>(undefined);
  const plansScrollGestureRef = useRef<GestureType | undefined>(undefined);
  const chatScrollGestureRef = useRef<GestureType | undefined>(undefined);
  const profileScrollGestureRef = useRef<GestureType | undefined>(undefined);
  const [chatSubscriptionsEnabled, setChatSubscriptionsEnabled] = useState(false);
  useEffect(() => {
    setChatSubscriptionsEnabled(false);
    if (!userId) return undefined;
    const timer = setTimeout(() => setChatSubscriptionsEnabled(true), 2500);
    return () => clearTimeout(timer);
  }, [userId]);
  const chatSubscriptionUserId = chatSubscriptionsEnabled ? userId : null;
  const { hasUnreadMessages, unreadChatCount } = useChatUnreadSummary(chatSubscriptionUserId);
  // Foreground in-app notifications — shows a banner when a new unmuted message arrives
  useChatNotifications(chatSubscriptionUserId);
  // usePushNotifications()/useNotificationListeners() moved to AppNavigator
  // (the parent of MainTabs) — MainTabs only exists in some AppNavigator
  // branches and unmounts/remounts across the requiresProfileSetup->final
  // transition, which was re-running useNotificationListeners()'s
  // getInitialNotification() call outside the one-time cold-start window
  // RNFB guarantees it for, and racing markAuthReady()/markNavReady() (set
  // from AppNavigator's own effects, which run AFTER a child's) on the
  // render where both first become eligible. See AppNavigator.
  const feedScrollGesture = useMemo(() => Gesture.Native().withRef(feedScrollGestureRef), []);
  const plansScrollGesture = useMemo(() => Gesture.Native().withRef(plansScrollGestureRef), []);
  const chatScrollGesture = useMemo(() => Gesture.Native().withRef(chatScrollGestureRef), []);
  const profileScrollGesture = useMemo(() => Gesture.Native().withRef(profileScrollGestureRef), []);

  useEffect(() => {
    if (!tabOrder.includes(requestedInitialTab)) return;
    activeIndexRef.current = tabOrder.indexOf(requestedInitialTab);
    setActiveTabName(requestedInitialTab);
  }, [requestedInitialTab, tabOrder]);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    nudgeSession.setActiveMainTab(activeTabName);
    return () => nudgeSession.setActiveMainTab(null);
  }, [activeTabName]);

  const finishMainSwitch = useCallback((routeName: keyof MainTabParamList, index: number) => {
    activeIndexRef.current = index;
    setActiveTabName(routeName);
  }, []);

  // Callers pass an inline arrow; a ref keeps switchMainScreen (and so every
  // memoized tab screen's props) stable across parent re-renders.
  const onOpenVerifyEmailRef = useRef(onOpenVerifyEmail);
  onOpenVerifyEmailRef.current = onOpenVerifyEmail;
  const switchMainScreen = useCallback((routeName: keyof MainTabParamList) => {
    const nextIndex = tabOrder.indexOf(routeName);
    if (nextIndex === -1) {
      // Tab is hidden because this account is still restricted (e.g. Map's
      // "Go to Discover" button) — surface the verify-email prompt instead
      // of silently doing nothing.
      onOpenVerifyEmailRef.current?.();
      return;
    }
    if (nextIndex === activeIndexRef.current) return;
    finishMainSwitch(routeName, nextIndex);
  }, [finishMainSwitch, tabOrder]);
  const goToDiscover = useCallback(() => switchMainScreen('Discover'), [switchMainScreen]);

  const handleCenterNavbarTap = useCallback(() => {
    setNavbarTapCount(count => Math.min(NAV_ONBOARDING_TAP_LIMIT, count + 1));
  }, []);

  const renderMainTabScreen = useCallback((routeName: keyof MainTabParamList) => {
    switch (routeName) {
      case 'Map':
        return (
          <MemoMapScreen
            userId={userId}
            role={role}
            onGoToDiscover={goToDiscover}
            focusLocation={focusMapLocation}
          />
        );
      case 'Discover':
        return <MemoDiscoverScreen userId={userId} />;
      case 'Feed':
        return <MemoFeedScreen userId={userId} scrollGesture={feedScrollGesture} focusFeed={focusFeed} />;
      case 'Plans':
        return <MemoPlansScreen userId={userId} scrollGesture={plansScrollGesture} />;
      case 'Chat':
        return <MemoChatListScreen userId={userId} scrollGesture={chatScrollGesture} focusMessagesTab={focusMessagesTab} />;
      case 'Profile':
        if (isPartner(role)) return <PartnerAccountProfileTab />;
        return (
          <MemoProfileScreen
            userId={userId}
            onSignOut={onSignOut}
            scrollGesture={imagePreviewActive ? undefined : profileScrollGesture}
            onImagePreviewActiveChange={setImagePreviewActive}
          />
        );
      default:
        return null;
    }
  }, [chatScrollGesture, feedScrollGesture, focusFeed, focusMapLocation, focusMessagesTab, goToDiscover, imagePreviewActive, onSignOut, plansScrollGesture, profileScrollGesture, role, userId]);

  const tabRoutes = useMemo(
    () => tabOrder.map(routeName => ({ key: routeName, name: routeName })),
    [tabOrder],
  );
  const tabBarState = useMemo(
    () => ({ index: activeIndex, routes: tabRoutes }),
    [activeIndex, tabRoutes],
  );
  const activeScreenSupportsDaylight =
    themeName === 'daylight' &&
    (activeTabName === 'Feed' ||
      activeTabName === 'Plans' ||
      activeTabName === 'Chat' ||
      activeTabName === 'Discover' ||
      activeTabName === 'Profile');

  return (
    <CityProvider userId={userId} role={role}>
      <DateProvider>
        <View style={[styles.mainTabsShell, mainTabsThemeStyles.shell]}>
          <StatusBar
            barStyle={activeScreenSupportsDaylight ? theme.statusBarStyle : 'light-content'}
            backgroundColor={activeScreenSupportsDaylight ? theme.colors.background : Colors.bg}
          />
          {restrictedAccess ? (
            <TouchableOpacity
              style={[styles.verifyBanner, { paddingTop: insets.top + 10 }]}
              activeOpacity={0.85}
              onPress={onOpenVerifyEmail}
            >
              <Text style={styles.verifyBannerText} numberOfLines={1}>
                {t('verifyEmail.limitedAccessBanner')}
              </Text>
              <Text style={styles.verifyBannerAction}>{t('verifyEmail.limitedAccessBannerAction')}</Text>
            </TouchableOpacity>
          ) : null}
          <View style={isDesktopWeb ? desktopStyles.desktopRow : styles.activeScreen}>
            {isDesktopWeb ? (
              <DesktopSidebar
                activeTab={activeTabName}
                tabs={tabOrder}
                unreadChatCount={unreadChatCount}
                onNavigate={switchMainScreen}
              />
            ) : null}
            <View style={isDesktopWeb ? desktopStyles.desktopMain : styles.activeScreen}>
              {isDesktopWeb ? <DesktopHeader activeTab={activeTabName} onNavigate={switchMainScreen} /> : null}
              <View collapsable={false} style={styles.activeScreen}>
              {tabOrder.filter(routeName => mountedTabs.has(routeName)).map(routeName => {
                const isActive = routeName === activeTabName;
                return (
                  <View
                    key={routeName}
                    collapsable={false}
                    style={[StyleSheet.absoluteFill, !isActive && styles.hiddenTabScreen]}
                    pointerEvents={isActive ? 'auto' : 'none'}
                  >
                    <TabActiveContext.Provider value={isActive}>
                      {renderMainTabScreen(routeName)}
                    </TabActiveContext.Provider>
                  </View>
                );
              })}
              </View>
            </View>
          </View>
          {!isDesktopWeb ? <CustomTabBar
            state={tabBarState}
            navigation={null}
            onNavigateTab={(routeName: keyof MainTabParamList) => switchMainScreen(routeName)}
            hasUnreadMessages={hasUnreadMessages}
            unreadChatCount={unreadChatCount}
            navbarTapCount={navbarTapCount}
            onCenterButtonTap={handleCenterNavbarTap}
            allowedTabs={restrictedAccess ? tabOrder : undefined}
          /> : null}
        </View>
      </DateProvider>
    </CityProvider>
  );
}

const DESKTOP_TAB_META: Record<keyof MainTabParamList, { icon: string; label: string }> = {
  Feed: { icon: 'calendar', label: 'Events' },
  Map: { icon: 'map', label: 'Map' },
  Discover: { icon: 'search', label: 'Discover' },
  Plans: { icon: 'clipboard', label: 'Plans' },
  Chat: { icon: 'message-square', label: 'Chat' },
  Profile: { icon: 'user', label: 'Profile' },
};

function DesktopSidebar({ activeTab, tabs, unreadChatCount, onNavigate }: {
  activeTab: keyof MainTabParamList;
  tabs: Array<keyof MainTabParamList>;
  unreadChatCount: number;
  onNavigate: (tab: keyof MainTabParamList) => void;
}) {
  const { theme, themeName, setThemeName } = useTheme();
  return (
    <View style={[desktopStyles.sidebar, { backgroundColor: theme.isDark ? '#171C1F' : '#FFFFFF', borderColor: theme.isDark ? '#30373B' : '#DED9D0' }]}>
      <Text style={[desktopStyles.wordmark, { color: theme.colors.textPrimary }]}>Tonyte</Text>
      <View style={desktopStyles.desktopNav}>
        {tabs.map(tab => {
          const meta = DESKTOP_TAB_META[tab];
          const active = tab === activeTab;
          return (
            <TouchableOpacity
              key={tab}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
              onPress={() => onNavigate(tab)}
              style={[desktopStyles.navItem, active && desktopStyles.navItemActive]}
            >
              <Icon name={meta.icon} size={17} color={active ? '#FFFFFF' : theme.colors.textSecondary} />
              <Text style={[desktopStyles.navLabel, { color: active ? '#FFFFFF' : theme.colors.textPrimary }]}>{meta.label}</Text>
              {tab === 'Chat' && unreadChatCount > 0 ? <View style={desktopStyles.sidebarBadge}><Text style={desktopStyles.sidebarBadgeText}>{unreadChatCount > 99 ? '99+' : unreadChatCount}</Text></View> : null}
            </TouchableOpacity>
          );
        })}
      </View>
      <TouchableOpacity
        accessibilityRole="switch"
        accessibilityState={{ checked: themeName === 'midnight' }}
        onPress={() => setThemeName(themeName === 'midnight' ? 'daylight' : 'midnight')}
        style={desktopStyles.themeToggle}
      >
        <View style={[desktopStyles.switchTrack, themeName === 'midnight' && desktopStyles.switchTrackOn]}>
          <View style={[desktopStyles.switchThumb, themeName === 'midnight' && desktopStyles.switchThumbOn]} />
        </View>
        <Text style={[desktopStyles.themeLabel, { color: theme.colors.textSecondary }]}>{themeName === 'midnight' ? 'Dark mode' : 'Light mode'}</Text>
      </TouchableOpacity>
    </View>
  );
}

function DesktopHeader({ activeTab, onNavigate }: { activeTab: keyof MainTabParamList; onNavigate: (tab: keyof MainTabParamList) => void }) {
  const { theme } = useTheme();
  return (
    <View style={[desktopStyles.desktopHeader, { backgroundColor: theme.isDark ? '#1C2225' : '#FFFFFF', borderColor: theme.isDark ? '#30373B' : '#DED9D0' }]}>
      <View style={[desktopStyles.locationControl, { borderColor: theme.colors.borderStrong }]}>
        <Icon name="map-pin" size={14} color={theme.colors.textSecondary} />
        <Text style={[desktopStyles.headerText, { color: theme.colors.textPrimary }]}>Dresden</Text>
        <Icon name="chevron-down" size={13} color={theme.colors.textMuted} />
      </View>
      <Text style={[desktopStyles.pageLabel, { color: theme.colors.textSecondary }]}>{DESKTOP_TAB_META[activeTab].label}</Text>
      <TouchableOpacity accessibilityLabel="Open profile" onPress={() => onNavigate('Profile')} style={[desktopStyles.profileButton, { borderColor: theme.colors.borderStrong }]}>
        <Icon name="user" size={16} color={theme.colors.textPrimary} />
      </TouchableOpacity>
    </View>
  );
}

const desktopStyles = StyleSheet.create({
  desktopRow: { flex: 1, flexDirection: 'row', minWidth: 0 },
  desktopMain: { flex: 1, minWidth: 0, minHeight: 0 },
  sidebar: { width: 176, borderRightWidth: 1, paddingHorizontal: 14, paddingTop: 20, paddingBottom: 18 },
  // Matches the marketing site's navbar .brand wordmark (Tonyte_website/src/styles.css):
  // Outfit at weight 600, -0.04em letter-spacing — was extrabold here, reading
  // noticeably heavier than the site even on the same typeface.
  wordmark: {
    fontSize: 24, fontWeight: '600', letterSpacing: -0.96, marginHorizontal: 10, marginBottom: 28,
    ...Platform.select({ web: { fontFamily: 'Outfit, sans-serif' } }),
  },
  desktopNav: { gap: 6 },
  navItem: { minHeight: 42, borderRadius: 9, paddingHorizontal: 12, flexDirection: 'row', alignItems: 'center', gap: 11 },
  navItemActive: { backgroundColor: '#F58538' },
  navLabel: { fontSize: 14, fontWeight: '600', flex: 1 },
  sidebarBadge: { minWidth: 19, height: 19, paddingHorizontal: 4, borderRadius: 10, backgroundColor: '#FFFFFF', alignItems: 'center', justifyContent: 'center' },
  sidebarBadgeText: { color: '#F58538', fontSize: 10, fontWeight: '800' },
  themeToggle: { marginTop: 'auto', minHeight: 40, paddingHorizontal: 8, flexDirection: 'row', alignItems: 'center', gap: 9 },
  switchTrack: { width: 30, height: 18, borderRadius: 9, padding: 2, backgroundColor: '#CCC7BE' },
  switchTrackOn: { backgroundColor: '#4E5B60' },
  switchThumb: { width: 14, height: 14, borderRadius: 7, backgroundColor: '#FFFFFF' },
  switchThumbOn: { alignSelf: 'flex-end' },
  themeLabel: { fontSize: 12, fontWeight: '500' },
  desktopHeader: { height: 52, borderBottomWidth: 1, paddingHorizontal: 18, flexDirection: 'row', alignItems: 'center', gap: 12 },
  locationControl: { height: 32, borderWidth: 1, borderRadius: 8, paddingHorizontal: 10, flexDirection: 'row', alignItems: 'center', gap: 6 },
  headerText: { fontSize: 12, fontWeight: '600' },
  pageLabel: { fontSize: 12, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 1, flex: 1, textAlign: 'center' },
  profileButton: { width: 32, height: 32, borderRadius: 16, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
});

// ─── Root Navigator ───────────────────────────────────────────────────────────

// Shown instead of guessing a destination when the post-auth Firestore
// profile fetch itself fails (e.g. offline, permission error) — the user is
// authenticated but we genuinely don't know their role/onboarding/
// verification state, so this is a dead end with a way back rather than a
// silently wrong screen.
function ProfileLoadErrorScreen({
  onRetry,
  onSignOut,
}: {
  onRetry: () => void;
  onSignOut: () => Promise<void>;
}) {
  const { t } = useI18n();
  return (
    <LinearGradient colors={['#0B1826', '#070B14', '#020409']} style={profileErrorStyles.container}>
      <Text style={profileErrorStyles.title}>{t('common.profileLoadErrorTitle')}</Text>
      <Text style={profileErrorStyles.message}>{t('common.profileLoadErrorMessage')}</Text>
      <TouchableOpacity style={profileErrorStyles.primaryButton} onPress={onRetry}>
        <Text style={profileErrorStyles.primaryButtonText}>{t('common.retry')}</Text>
      </TouchableOpacity>
      <TouchableOpacity style={profileErrorStyles.secondaryButton} onPress={() => onSignOut()}>
        <Text style={profileErrorStyles.secondaryButtonText}>{t('common.logOut')}</Text>
      </TouchableOpacity>
    </LinearGradient>
  );
}

const profileErrorStyles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Spacing.xl,
  },
  title: {
    color: Colors.textPrimary,
    fontSize: Typography.lg,
    fontWeight: Typography.semibold,
    marginBottom: Spacing.sm,
    textAlign: 'center',
  },
  message: {
    color: Colors.textSecondary,
    fontSize: Typography.sm,
    textAlign: 'center',
    marginBottom: Spacing.xl,
  },
  primaryButton: {
    backgroundColor: Colors.sunset,
    paddingVertical: Spacing.md,
    paddingHorizontal: Spacing.xxl,
    borderRadius: 999,
    marginBottom: Spacing.md,
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: Typography.md,
    fontWeight: Typography.semibold,
  },
  secondaryButton: {
    paddingVertical: Spacing.sm,
    paddingHorizontal: Spacing.xl,
  },
  secondaryButtonText: {
    color: Colors.textSecondary,
    fontSize: Typography.sm,
    fontWeight: Typography.medium,
  },
});

interface Props {
  user: null | { uid: string; email: string | null };
  loading: boolean;
  isSigningIn: boolean;
  emailVerified: boolean;
  isEmailPasswordAccount: boolean;
  hasTrustedLinkedProvider: boolean;
  onEmailSignIn: (email: string, password: string, mode: 'signin' | 'signup') => Promise<void>;
  onCompletePartnerPasswordSetup: ReturnType<typeof useAuth>['completePartnerPasswordSetup'];
  onPasswordReset: (email: string) => Promise<void>;
  onGoogleSignIn: () => Promise<void>;
  onAppleSignIn?: () => Promise<void>;
  onSignOut: () => Promise<void>;
  onResendVerificationEmail: () => Promise<void>;
  onCheckEmailVerification: () => Promise<boolean>;
}

export function AppNavigator({
  user,
  loading,
  isSigningIn,
  emailVerified,
  isEmailPasswordAccount,
  hasTrustedLinkedProvider,
  onEmailSignIn,
  onCompletePartnerPasswordSetup,
  onPasswordReset,
  onGoogleSignIn,
  onAppleSignIn,
  onSignOut,
  onResendVerificationEmail,
  onCheckEmailVerification,
}: Props) {
  const { t } = useI18n();
  const userId = user?.uid ?? null;
  const isAuthenticated = user !== null;
  const {
    profile,
    loading: profileLoading,
    error: profileError,
    retry: retryProfile,
  } = useProfile(isAuthenticated ? userId : null);
  // Race-free readiness signal: normalizeProfile() always stamps `id: uid` on
  // the profile it returns, so this can only be true once a snapshot for
  // THIS specific user has actually arrived — unlike `profileLoading` alone,
  // which is a plain useState boolean set inside an effect and therefore
  // still reads its stale prior value (false) for exactly one render right
  // as `userId` changes. That one stale render is what let the navigator
  // below fall through to the fully-authenticated branch with `profile` still
  // null right after login — the reported flash of Home/Map before the
  // correct destination.
  const profileReadyForCurrentUser = Boolean(profile && profile.id === userId);
  const isPartnerAccount = isPartner(profile?.role);
  const { partnerProfile, loading: partnerProfileLoading } = usePartnerProfile(
    isAuthenticated && profileReadyForCurrentUser && isPartnerAccount ? userId : null,
  );
  // Only meaningful for accounts we've confirmed (via a fresh profile) are
  // partners — usePartnerProfile is never given a real uid otherwise, so
  // partnerProfileLoading stays false and never blocks non-partner accounts.
  const partnerGateReady = !isPartnerAccount || !partnerProfileLoading;

  // The single source of truth for "what should the root navigator show".
  // Mirrors the user-facing model of initializing -> signingIn -> loadingUser
  // -> authenticated (or unauthenticated once initializing resolves with no
  // session) — evaluated top-to-bottom so an earlier phase always wins.
  type AuthPhase =
    | 'initializing'
    | 'signingIn'
    | 'loadingUser'
    | 'profileError'
    | 'authenticated'
    | 'unauthenticated';
  const authPhase: AuthPhase = loading
    ? 'initializing'
    : !isAuthenticated
      ? (isSigningIn ? 'signingIn' : 'unauthenticated')
      : profileError && !profileReadyForCurrentUser
        ? 'profileError'
        : !profileReadyForCurrentUser || !partnerGateReady
          ? 'loadingUser'
          : 'authenticated';
  const isProfileReady = authPhase === 'authenticated';

  // Partner accounts are provisioned by Tonyte and accept the Partner Terms
  // on activation; the personal onboarding (date of birth, student
  // verification, interests) doesn't apply to a business, so they skip it.
  const requiresProfileSetup = Boolean(
    isAuthenticated &&
    !profileLoading &&
    profile &&
    !isPartnerAccount &&
    !profile.onboardingComplete &&
    !profile.profileSetupCompleted,
  );
  // Only the public self-serve email/password signup (LoginScreen's email
  // modal) ever self-assigns role 'member' with a password provider —
  // partner/organizer/venue/admin/co-admin accounts are always provisioned
  // through separate administrative flows (invitation acceptance, manual
  // role grants), so keying this off role as well as provider means those
  // accounts can never be locked out by this gate even if an Admin-SDK-
  // created account happens to have emailVerified: false.
  const requiresEmailVerification = Boolean(
    isAuthenticated &&
    !profileLoading &&
    profile &&
    isEmailPasswordAccount &&
    // Account-linking guard: providerData lists every provider ever linked
    // to this account, not just the one used for the current sign-in — a
    // password account that has since linked Google/Apple keeps 'password'
    // in that list forever. Without this, re-signing-in via the linked
    // Google/Apple credential (or even the password credential, once
    // trusted) would still wrongly show the verification gate.
    !hasTrustedLinkedProvider &&
    !emailVerified &&
    normalizeUserRole(profile.role) === 'member',
  );
  // Partners must set their own password before anything else — including
  // profile setup or the normal app — the very first time they sign in.
  const requiresPartnerPasswordSetup = Boolean(
    isAuthenticated &&
    !profileLoading &&
    isPartnerAccount &&
    !partnerProfileLoading &&
    partnerProfile?.mustChangePassword,
  );
  const showUpdatePrompt = Boolean(
    isProfileReady &&
    !requiresProfileSetup &&
    !requiresPartnerPasswordSetup,
  );

  // Broadcasts the restriction to the module-level verificationGate
  // singleton so write hooks that live outside this component tree
  // (useVenueVibes.ts, useVibes.ts, usePlanJoinRequest.ts, sendChatMessage,
  // etc.) can block a social write and show a friendly prompt without
  // needing auth/profile state threaded through every one of them —
  // AppNavigator is the one place both are already available together.
  useEffect(() => {
    setSocialWriteRestricted(requiresEmailVerification);
  }, [requiresEmailVerification]);

  // True only once the tree is actually rendering the final, fully-
  // authenticated branch below that registers `PublicProfile`/`EventDetail`/
  // `VenueProfile`/`OrganizerProfile`/`ChatDetail`/`PlanRequests`/
  // `StudiZoneProfileRequests` — i.e. authenticated, profile loaded, and none
  // of profile-setup/partner-password-setup/email-verification still
  // pending. Previously missing `!requiresEmailVerification`: those chat/
  // plan-request/profile screens are registered ONLY in this final branch,
  // not the email-verification branch, so an email-unverified user's
  // markMainReady()/markAuthReady() (below) fired while the tree still
  // couldn't reach them — the queued push-tap or share-link navigation
  // flushed and silently no-op'd. Reproduced/confirmed as one of the actual
  // causes of "notification taps don't reliably navigate."
  const isMainStackMounted = Boolean(
    isProfileReady &&
    !requiresPartnerPasswordSetup &&
    !requiresProfileSetup &&
    !requiresEmailVerification,
  );

  useEffect(() => {
    if (isMainStackMounted) markMainReady();
  }, [isMainStackMounted]);

  // "Ready" for notification-tap navigation means the screen the tap wants
  // to reach actually exists in the currently-rendered tree — NOT just that
  // Firebase Auth itself has resolved. Deliberately NOT derived from
  // `showUpdatePrompt` (which is also wired to <AppUpdatePrompt> — reusing
  // it here would have silently changed when that unrelated update-nag
  // shows for email-unverified users). Reuses the now-corrected
  // `isMainStackMounted` above instead of a separately-maintained, easy-to-
  // drift-apart copy of the same condition. Also fires for a definitively
  // signed-out user so the queue doesn't hang forever waiting for a
  // readiness signal that will never come (navigation then safely
  // no-ops/falls back, same as before).
  const mainNavigatorReady = !loading && (!isAuthenticated || isMainStackMounted);

  useEffect(() => {
    if (mainNavigatorReady) markAuthReady();
  }, [mainNavigatorReady]);

  // Both moved here from MainTabs (a child that only exists in some of the
  // branches below and unmounts/remounts across the
  // requiresProfileSetup->final transition) — mounted exactly once here, in
  // the same component that sets markAuthReady()/markNavReady(), so there's
  // no child-before-parent effect race and no risk of losing a cold-start
  // tap to a remount. useNotificationListeners() is unconditional: it needs
  // to start listening for getInitialNotification()/onNotificationOpenedApp
  // as early as possible regardless of auth state — notificationNav.ts's own
  // pendingRun queue is what safely holds the result until navReady/
  // authReady actually open, not this hook's mount timing.
  useNotificationListeners();
  // Tied to isMainStackMounted rather than MainTabs' old arbitrary 2.5s
  // timer — guarantees the permission prompt/token registration never fires
  // mid-onboarding (profile setup / partner password / email verification
  // still pending) without needing a magic-number delay to approximate that.
  usePushNotifications(isMainStackMounted ? userId : null);

  // Broader than isMainStackMounted: `StudentVerificationComplete` is
  // registered both in the requiresProfileSetup branch below AND in the
  // final branch — a user who hasn't finished onboarding yet can still
  // legitimately tap a verification email link — so this deliberately omits
  // the `!requiresProfileSetup` condition that isMainStackMounted has.
  const isVerificationStackMounted = Boolean(
    isProfileReady &&
    !requiresPartnerPasswordSetup,
  );

  useEffect(() => {
    if (isVerificationStackMounted) markVerificationReady();
  }, [isVerificationStackMounted]);

  // Web-only: @react-navigation/native's own web `useLinking` implementation
  // (lib/module/useLinking.js, distinct from useLinking.native.js) reads
  // window.location directly and resolves it against `config.screens`
  // itself, ONCE, synchronously, at NavigationContainer's first mount —
  // completely bypassing LINKING_CONFIG.getInitialURL above (confirmed via
  // that function's own logging never firing on web). At that first-mount
  // instant the only registered screen is Login (auth hasn't resolved yet),
  // so EventDetail/PublicProfile/etc. never match and the URL is lost for
  // good — react-navigation never re-attempts it once those screens
  // register later. This independently re-parses the page's original URL
  // (once, on mount) and feeds it into the exact same queueDeepLink/
  // mainReady gate native already relies on, so a cold-loaded
  // /event/:eventId, /profile/:userId, /venue/:venueId, /organizer/:organizerId
  // (or /verify-student/:token) resolves once the destination screen is
  // actually mounted, instead of being silently dropped.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    // Accessed via globalThis (rather than the bare `window` identifier) so
    // this shared, non-.web-suffixed file still type-checks cleanly under
    // the native tsconfig, which has no DOM lib — this branch never runs on
    // native anyway (Platform.OS !== 'web' above).
    const browserWindow = (globalThis as { window?: { location?: { href?: string } } }).window;
    if (!browserWindow?.location?.href) return;
    const normalizedUrl = normalizeStudentVerificationUrl(browserWindow.location.href);
    const target = extractDeepLinkTarget(normalizedUrl);
    if (target) {
      console.log('[Tonyte][DeepLink][web] initial url', normalizedUrl, target);
      queueDeepLink(target);
    }
    // Intentionally run once on mount only — this captures the URL the page
    // was *loaded* with, not subsequent in-app navigation (which already
    // updates the address bar via react-navigation's own web integration).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!DEBUG_NAVIGATION_GATE) return;
    console.log('[Tonyte][NavigationGate] access state evaluated', {
      userId,
      isAuthenticated,
      authLoading: loading,
      isSigningIn,
      authPhase,
      profileLoading,
      profileReadyForCurrentUser,
      profileExists: Boolean(profile),
      role: profile?.role || null,
      requiresProfileSetup,
    });
  }, [
    isAuthenticated,
    loading,
    isSigningIn,
    authPhase,
    profileReadyForCurrentUser,
    profile,
    profileLoading,
    requiresProfileSetup,
    userId,
  ]);

  return (
    <ThemeProvider>
    <NavigationContainer
      ref={navigationRef}
      linking={LINKING_CONFIG}
      onReady={markNavReady}
      onStateChange={() => {
        // [PushTapDebug] step 9 — proves whether a navigate() call actually
        // entered the navigation state (vs. silently no-op'd against an
        // unregistered route). Temporary, remove after the debug pass.
        if (__DEV__) console.log('[PushTapDebug] nav state changed, current route =', navigationRef.getCurrentRoute()?.name ?? null);
      }}
    >
      <Stack.Navigator screenOptions={{ headerShown: false }}>
        {authPhase === 'initializing' || authPhase === 'loadingUser' ? (
          // Covers cold-start auth restoration and the window after Firebase
          // auth succeeds but before we know this user's profile/role/
          // onboarding/verification state — every moment where the
          // destination isn't yet known. Never render Home/Map/etc. here.
          // (The 'signingIn' phase is handled inside LoginScreen itself, as
          // an overlay, so a failed attempt doesn't remount away the form —
          // see isSigningIn below.)
          <Stack.Screen name="Login">
            {() => <TonyteLoadingScreen message={t('common.findingYourVibe')} />}
          </Stack.Screen>
        ) : authPhase === 'profileError' ? (
          <Stack.Screen name="Login">
            {() => (
              <ProfileLoadErrorScreen
                onRetry={retryProfile}
                onSignOut={onSignOut}
              />
            )}
          </Stack.Screen>
        ) : authPhase === 'unauthenticated' || authPhase === 'signingIn' ? (
          <Stack.Screen name="Login">
            {() => (
              <LoginScreen
                isSigningIn={isSigningIn}
                onEmailSignIn={onEmailSignIn}
                onPasswordReset={onPasswordReset}
                onGoogleSignIn={onGoogleSignIn}
                onAppleSignIn={onAppleSignIn ?? (async () => {})}
              />
            )}
          </Stack.Screen>
        ) : requiresPartnerPasswordSetup ? (
          <Stack.Screen name="PartnerSetPassword">
            {() => (
              <PartnerSetPasswordScreen
                onCompletePartnerPasswordSetup={onCompletePartnerPasswordSetup}
                onSignOut={onSignOut}
              />
            )}
          </Stack.Screen>
        ) : requiresProfileSetup ? (
          <>
            <Stack.Screen name="ProfileSetup">
              {props => (
                <ProfileSetupScreen
                  {...props}
                  route={{
                    ...props.route,
                    params: {
                      uid: userId || '',
                      email: profile?.email || '',
                      name: profile?.name || '',
                      photo: profile?.photo || '',
                    },
                  }}
                />
              )}
            </Stack.Screen>
            <Stack.Screen name="Main">
              {props => (
                <MainTabs
                  userId={userId}
                  onSignOut={onSignOut}
                  initialTab={props.route.params?.initialTab}
                  role={profile?.role}
                  focusMapLocation={props.route.params?.focusMapLocation}
                  focusMessagesTab={props.route.params?.focusMessagesTab}
                  focusFeed={props.route.params?.focusFeed}
                />
              )}
            </Stack.Screen>
            <Stack.Screen name="TermsOfService" component={TermsOfServiceScreen} />
            <Stack.Screen name="PrivacyPolicy" component={PrivacyPolicyScreen} />
            <Stack.Screen name="Impressum" component={ImpressumScreen} />
            <Stack.Screen name="PartnerTerms" component={PartnerTermsScreen} />
            <Stack.Screen name="CommunityGuidelines" component={CommunityGuidelinesScreen} />
            <Stack.Screen name="VerificationCenter" component={VerificationCenterScreen} />
            <Stack.Screen name="StudentVerificationComplete" component={StudentVerificationCompleteScreen} />
          </>
        ) : requiresEmailVerification ? (
          <>
            <Stack.Screen name="VerifyEmail">
              {props => (
                <VerifyEmailScreen
                  email={user?.email ?? null}
                  onResend={onResendVerificationEmail}
                  onCheckAgain={onCheckEmailVerification}
                  onSignOut={onSignOut}
                  onContinueBrowsing={() => props.navigation.navigate('Main', undefined)}
                />
              )}
            </Stack.Screen>
            <Stack.Screen name="Main">
              {props => (
                <MainTabs
                  userId={userId}
                  onSignOut={onSignOut}
                  initialTab={props.route.params?.initialTab}
                  role={profile?.role}
                  focusMapLocation={props.route.params?.focusMapLocation}
                  focusMessagesTab={props.route.params?.focusMessagesTab}
                  focusFeed={props.route.params?.focusFeed}
                  restrictedAccess
                  onOpenVerifyEmail={() => props.navigation.navigate('VerifyEmail')}
                />
              )}
            </Stack.Screen>
            <Stack.Screen name="EventDetail" component={EventDetailScreen} />
            <Stack.Screen name="TermsOfService" component={TermsOfServiceScreen} />
            <Stack.Screen name="PrivacyPolicy" component={PrivacyPolicyScreen} />
            <Stack.Screen name="Impressum" component={ImpressumScreen} />
            <Stack.Screen name="PartnerTerms" component={PartnerTermsScreen} />
            <Stack.Screen name="CommunityGuidelines" component={CommunityGuidelinesScreen} />
            <Stack.Screen name="VerificationCenter" component={VerificationCenterScreen} />
            <Stack.Screen name="StudentVerificationComplete" component={StudentVerificationCompleteScreen} />
            <Stack.Screen name="Settings" component={SettingsScreen} />
            <Stack.Screen name="EditProfile" component={EditProfileScreen} />
          </>
        ) : (
          <>
            <Stack.Screen name="Main">
              {props => (
                <MainTabs
                  userId={userId}
                  onSignOut={onSignOut}
                  initialTab={props.route.params?.initialTab}
                  role={profile?.role}
                  focusMapLocation={props.route.params?.focusMapLocation}
                  focusMessagesTab={props.route.params?.focusMessagesTab}
                  focusFeed={props.route.params?.focusFeed}
                />
              )}
            </Stack.Screen>
            <Stack.Screen name="EventDetail" component={EventDetailScreen} />
            <Stack.Screen name="StudiZoneEventDetail" component={EventDetailScreen} />
            <Stack.Screen name="StudiZonePlanDetail" component={EventDetailScreen} />
            <Stack.Screen name="StudiZonePlanDetailScreen" component={EventDetailScreen} />
            <Stack.Screen name="StudiZonePlanChat" component={ChatDetailScreen} />
            <Stack.Screen name="StudiZonePlanChatScreen" component={ChatDetailScreen} />
            <Stack.Screen name="StudiZoneCreatePlan" component={UploadPlanScreen} />
            <Stack.Screen name="StudiZoneCreatePlanScreen" component={UploadPlanScreen} />
            <Stack.Screen name="StudiZoneProfileRequests" component={PlanRequestsScreen} />
            <Stack.Screen name="StudiZoneProfileRequestsScreen" component={PlanRequestsScreen} />
            <Stack.Screen name="StudiZonePlansScreen" component={StudiZoneScreen} />
            <Stack.Screen name="StudiZoneMap" component={StudiZoneMapScreen} />
            <Stack.Screen name="VibeVenues" component={VibeVenuesScreen} />
            <Stack.Screen name="VenueProfile" component={VenueProfileScreen} />
            <Stack.Screen name="VenueAnalytics" component={VenueAnalyticsScreen} />
            <Stack.Screen name="EditVenueProfile" component={EditVenueProfileScreen} />
            <Stack.Screen name="AddPartner" component={AddPartnerScreen} />
            <Stack.Screen name="FollowingPartners" component={FollowingPartnersScreen} />
            <Stack.Screen name="OrganizerProfile" component={OrganizerProfileScreen} />
            <Stack.Screen name="PublicProfile" component={PublicIdentityRouterScreen} />
            <Stack.Screen name="Vibes" component={VibesScreen} />
            <Stack.Screen name="AllTopPicks" component={AllTopPicksScreen} />
            <Stack.Screen name="Promotion" component={PromotionScreen} />
            <Stack.Screen name="AdminPromotions" component={AdminPromotionsScreen} />
            <Stack.Screen name="AdminReviewReports" component={AdminReviewReportsScreen} />
            <Stack.Screen name="AdminPromotionEditor" component={AdminPromotionEditorScreen} />
            <Stack.Screen name="ChatDetail" component={ChatDetailScreen} />
            <Stack.Screen name="UploadEvent" component={UploadEventScreen} />
            <Stack.Screen name="UploadPlan" component={UploadPlanScreen} />
            <Stack.Screen name="StudiZone" component={StudiZoneScreen} />
            <Stack.Screen name="EditProfile" component={EditProfileScreen} />
            <Stack.Screen name="Settings" component={SettingsScreen} />
            <Stack.Screen name="VerificationCenter" component={VerificationCenterScreen} />
            <Stack.Screen name="MyTemplates" component={MyTemplatesScreen} />
            <Stack.Screen name="MyPosts" component={MyPostsScreen} />
            <Stack.Screen name="PartnerHome" component={PartnerHomeScreen} />
            <Stack.Screen name="PartnerAccountProfile" component={PartnerAccountProfileScreen} />
            <Stack.Screen name="PartnerAnalytics" component={PartnerAnalyticsScreen} />
            <Stack.Screen name="PartnerNotifications" component={PartnerNotificationsScreen} />
            <Stack.Screen name="CalendarImports" component={CalendarImportsScreen} />
            <Stack.Screen name="PartnerCalendar" component={PartnerCalendarScreen} />
            <Stack.Screen name="PlanRequests" component={PlanRequestsScreen} />
            <Stack.Screen name="CompleteProfile" component={CompleteProfileScreen} />
            <Stack.Screen name="DayVibesQuestion" component={DayVibesQuestionScreen} />
            <Stack.Screen name="DayVibesAura" component={DayVibesAuraScreen} />
            <Stack.Screen name="TermsOfService" component={TermsOfServiceScreen} />
            <Stack.Screen name="PrivacyPolicy" component={PrivacyPolicyScreen} />
            <Stack.Screen name="Impressum" component={ImpressumScreen} />
            <Stack.Screen name="PartnerTerms" component={PartnerTermsScreen} />
            <Stack.Screen name="CommunityGuidelines" component={CommunityGuidelinesScreen} />
            <Stack.Screen name="StudentVerificationComplete" component={StudentVerificationCompleteScreen} />
          </>
        )}
        <Stack.Screen name="ResetPassword">
          {props => (
            <ResetPasswordScreen
              {...props}
              onContinueToLogin={onSignOut}
            />
          )}
        </Stack.Screen>
        <Stack.Screen name="VerifyEmailComplete">
          {props => (
            <VerifyEmailCompleteScreen
              {...props}
              onCheckEmailVerification={onCheckEmailVerification}
              onContinueToLogin={onSignOut}
            />
          )}
        </Stack.Screen>
      </Stack.Navigator>
      <AppUpdatePrompt enabled={showUpdatePrompt} />
      {/* Optional nudges (survey, profile photo, Vibe Verified, store review): main app only, never during onboarding/verification. */}
      <OptionalNudgeHost enabled={isMainStackMounted} userId={isMainStackMounted ? userId : null} profile={profile ?? null} />
      {/* Web Vibe game answers → this account (go.tonyte.app links, Play install referrer, recovery code, verified email). */}
      <VibeClaimCoordinator ready={isMainStackMounted} userId={isMainStackMounted ? userId : null} />
      <TonyteConfirmationHost />
    </NavigationContainer>
    </ThemeProvider>
  );
}

const createNavThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  navDim: daylightStyle(theme, { backgroundColor: theme.colors.overlay }),
  navCircleWrap: daylightStyle(theme, {
    backgroundColor: '#F1E5D5',
    borderColor: 'rgba(123,94,64,0.22)',
    borderWidth: 1,
    ...theme.shadows.navigation,
  }),
  navUpperGlass: daylightStyle(theme, {
    backgroundColor: 'rgba(255,253,247,0.20)',
    borderColor: 'rgba(255,255,255,0.76)',
  }),
  navInnerShade: daylightStyle(theme, {
    borderColor: 'rgba(126,96,68,0.18)',
    backgroundColor: 'rgba(111,78,48,0.025)',
  }),
  dialGuideOuter: daylightStyle(theme, {
    borderColor: 'rgba(125,96,68,0.19)',
    backgroundColor: 'transparent',
  }),
  arcLine: daylightStyle(theme, {
    borderColor: 'rgba(255,255,255,0.54)',
    borderBottomColor: 'rgba(126,96,68,0.12)',
  }),
  tick: daylightStyle(theme, { backgroundColor: 'rgba(123,91,62,0.42)' }),
  tickSelectedSegment: daylightStyle(theme, {
    backgroundColor: 'rgba(220,103,24,0.66)',
  }),
  tickSelectedCore: daylightStyle(theme, {
    backgroundColor: '#E56816',
    shadowColor: '#E56816',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.34,
    shadowRadius: 5,
    elevation: 2,
  }),
  compassButton: daylightStyle(theme, {
    backgroundColor: '#E9DBC8',
    borderColor: 'rgba(116,85,55,0.30)',
    shadowColor: '#806344',
    shadowOffset: { width: 0, height: 5 },
    shadowOpacity: 0.16,
    shadowRadius: 9,
    elevation: 6,
  }),
  compassButtonActive: daylightStyle(theme, {
    backgroundColor: '#F0D3B3',
    borderColor: '#D96518',
    borderWidth: 2,
    shadowColor: '#E56816',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.31,
    shadowRadius: 11,
    elevation: 9,
  }),
  compassButtonFill: daylightStyle(theme, {
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.70)',
  }),
  compassButtonReflection: daylightStyle(theme, {
    backgroundColor: 'rgba(255,255,255,0.58)',
  }),
  compassLabel: daylightStyle(theme, { color: '#665C52' }),
  compassLabelActive: daylightStyle(theme, { color: '#D95E13', fontWeight: '900' }),
  centerButton: daylightStyle(theme, {
    backgroundColor: '#0B1220',
    borderColor: 'rgba(224,126,46,0.82)',
    shadowColor: '#A85D26',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.30,
    shadowRadius: 15,
    elevation: 13,
  }),
});

const createMainTabsThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  shell: daylightStyle(theme, { backgroundColor: theme.colors.background }),
});

const styles = StyleSheet.create({
  navDim: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: -1000,
    bottom: 0,
    zIndex: 24,
    backgroundColor: 'rgba(3,6,14,0.6)',
  },
  tabRoot: {
    position: 'absolute',
    left: '50%',
    zIndex: 30,
  },
  navClip: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: NAV_HEIGHT,
    overflow: 'hidden',
  },
  navCircleWrap: {
    position: 'absolute',
    overflow: 'hidden',
    backgroundColor: 'rgba(3,6,14,0.9)',
    borderWidth: 0,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -12 },
    shadowOpacity: Platform.OS === 'ios' ? 0.42 : 0.56,
    shadowRadius: Platform.OS === 'ios' ? 28 : 32,
    elevation: 18,
  },
  navCircle: {
    ...StyleSheet.absoluteFill,
  },
  navEdgeTint: {
    ...StyleSheet.absoluteFill,
    opacity: Platform.OS === 'ios' ? 0.58 : 0.76,
  },
  navGlassSurface: {
    ...StyleSheet.absoluteFill,
    opacity: Platform.OS === 'ios' ? 0.72 : 0.58,
  },
  navWarmBlend: {
    ...StyleSheet.absoluteFill,
    opacity: Platform.OS === 'ios' ? 0.38 : 0.56,
  },
  navUpperGlass: {
    ...StyleSheet.absoluteFill,
    backgroundColor: Platform.OS === 'ios' ? 'rgba(255,255,255,0.018)' : 'rgba(255,255,255,0.012)',
    borderTopWidth: 1,
    borderColor: Platform.OS === 'ios' ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.10)',
  },
  navInnerShade: {
    ...StyleSheet.absoluteFill,
    borderRadius: NAV_RADIUS,
    borderWidth: 1,
    borderColor: Platform.OS === 'ios' ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.09)',
    backgroundColor: 'rgba(0,0,0,0.045)',
  },
  arcLine: {
    position: 'absolute',
    width: 210,
    height: 210,
    borderRadius: 105,
    borderTopWidth: 1,
    borderColor: 'rgba(255,255,255,0.06)',
  },
  dialGuideOuter: {
    position: 'absolute',
    width: 328,
    height: 328,
    borderRadius: 164,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.035)',
  },
  tick: {
    position: 'absolute',
    borderRadius: 2,
    backgroundColor: 'rgba(230,238,246,0.84)',
  },
  tickHighlighted: {
    backgroundColor: 'rgba(242,125,38,0.95)',
    shadowColor: '#F27D26',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.7,
    shadowRadius: 8,
  },
  compassItem: {
    position: 'absolute',
    width: 44,
    height: 60,
    alignItems: 'center',
    gap: 3,
  },
  compassHit: {
    width: 54,
    height: 64,
    alignItems: 'center',
  },
  compassButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 1.6,
    borderColor: 'rgba(215,225,235,0.32)',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(4,8,15,0.78)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: Platform.OS === 'ios' ? 0.24 : 0.32,
    shadowRadius: Platform.OS === 'ios' ? 14 : 16,
    elevation: 8,
  },
  compassButtonFill: {
    flex: 1,
    width: '100%',
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  compassButtonReflection: {
    position: 'absolute',
    top: 3,
    left: 8,
    right: 8,
    height: 1,
    borderRadius: 1,
    backgroundColor: 'rgba(255,255,255,0.12)',
  },
  compassButtonActive: {
    borderColor: 'rgba(255,142,38,0.82)',
    shadowColor: '#F27D26',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: Platform.OS === 'ios' ? 0.42 : 0.58,
    shadowRadius: Platform.OS === 'ios' ? 16 : 19,
    elevation: 12,
  },
  compassButtonHovered: {
    borderColor: 'rgba(255,144,48,0.78)',
    shadowColor: '#F27D26',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.46,
    shadowRadius: 14,
    elevation: 10,
  },
  compassButtonLocked: {
    borderColor: 'rgba(242,125,38,0.95)',
    transform: [{ scale: 1.08 }],
    shadowColor: '#F27D26',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.68,
    shadowRadius: 20,
    elevation: 14,
  },
  compassButtonPulsing: {
    borderColor: 'rgba(242,125,38,0.88)',
    transform: [{ scale: 1.14 }],
    shadowColor: '#F27D26',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.58,
    shadowRadius: 22,
    elevation: 14,
  },
  compassLabel: {
    color: 'rgba(221,227,234,0.58)',
    fontSize: 9,
    fontWeight: '800',
    textAlign: 'center',
    textTransform: 'uppercase',
    marginTop: 7,
  },
  compassLabelActive: {
    color: '#ff8a22',
  },
  unreadDot: {
    position: 'absolute',
    top: 7,
    right: 7,
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: Colors.sunset,
  },
  unreadCountBadge: {
    position: 'absolute',
    top: 4,
    right: 4,
    minWidth: 15,
    height: 15,
    borderRadius: 8,
    paddingHorizontal: 4,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.sunset,
    borderWidth: 1,
    borderColor: 'rgba(4,8,15,0.92)',
  },
  unreadCountText: {
    color: '#fff',
    fontSize: 8,
    lineHeight: 10,
    fontWeight: '900',
  },
  centerSpotlight: {
    position: 'absolute',
    width: 230,
    height: 126,
    borderRadius: 115,
    backgroundColor: 'rgba(255,175,84,0.18)',
  },
  centerAura: {
    position: 'absolute',
    width: 192,
    height: 192,
    borderRadius: 96,
    overflow: 'hidden',
    backgroundColor: 'rgba(242,125,38,0.08)',
    shadowColor: '#F27D26',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: Platform.OS === 'ios' ? 0.48 : 0.62,
    shadowRadius: Platform.OS === 'ios' ? 44 : 36,
    elevation: 10,
  },
  centerAuraFill: {
    flex: 1,
    borderRadius: 96,
  },
  centerOuterRing: {
    position: 'absolute',
    width: 118,
    height: 118,
    borderRadius: 59,
    borderWidth: 1,
    borderColor: 'rgba(255,190,105,0.72)',
    backgroundColor: 'rgba(242,125,38,0.018)',
    shadowColor: '#F27D26',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: Platform.OS === 'ios' ? 0.46 : 0.58,
    shadowRadius: Platform.OS === 'ios' ? 20 : 16,
    elevation: 8,
  },
  centerSparkOrbit: {
    position: 'absolute',
    width: 160,
    height: 160,
  },
  centerSparkTrack: {
    position: 'absolute',
    left: 78,
    top: 78,
    width: 4,
    height: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  centerSpark: {
    backgroundColor: 'rgba(255,224,168,0.96)',
    shadowColor: '#FFB45A',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: Platform.OS === 'ios' ? 0.82 : 0.92,
    shadowRadius: Platform.OS === 'ios' ? 7 : 5,
    elevation: 6,
  },
  centerButtonShell: {
    position: 'absolute',
  },
  centerButton: {
    width: 58,
    height: 58,
    borderRadius: 29,
    backgroundColor: 'rgba(4,7,14,0.88)',
    borderWidth: 2,
    borderColor: 'rgba(255,165,70,0.65)',
    padding: 0,
    shadowColor: '#F27D26',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: Platform.OS === 'ios' ? 0.5 : 0.68,
    shadowRadius: Platform.OS === 'ios' ? 24 : 28,
    elevation: 18,
  },
  centerButtonOnboarding: {
    borderColor: 'rgba(255,196,112,0.92)',
    shadowColor: '#F27D26',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: Platform.OS === 'ios' ? 0.78 : 0.9,
    shadowRadius: Platform.OS === 'ios' ? 34 : 32,
    elevation: 24,
  },
  centerButtonOpen: {
    width: 82,
    height: 82,
    borderRadius: 41,
    borderColor: 'rgba(242,125,38,0.76)',
  },
  centerButtonFill: {
    flex: 1,
    borderRadius: 29,
    alignItems: 'center',
    justifyContent: 'center',
  },
  centerButtonFillOpen: {
    borderRadius: 41,
  },
  mainTabsShell: {
    flex: 1,
    backgroundColor: '#02050b',
  },
  verifyBanner: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: Spacing.sm,
    paddingHorizontal: Spacing.base,
    paddingBottom: 10,
    backgroundColor: 'rgba(255,122,0,0.94)',
  },
  verifyBannerText: {
    flex: 1,
    color: '#fff',
    fontSize: Typography.xs,
    fontWeight: Typography.semibold,
  },
  verifyBannerAction: {
    color: '#fff',
    fontSize: Typography.xs,
    fontWeight: Typography.extrabold,
    textDecorationLine: 'underline',
  },
  activeScreen: {
    flex: 1,
  },
  // NOT `display: 'none'` — collapsing a hidden tab's native view to zero
  // size/detached tears down the WebView-hosted Map screen's Leaflet
  // instance (tiles + markers) on Android and can force a reload on iOS too,
  // so returning to Map re-ran the entire init from scratch every time.
  // Opacity keeps the native view (and its WebView surface) alive and
  // attached at full size the whole session; pointerEvents:'none' (applied
  // alongside this style above) already keeps it non-interactive.
  hiddenTabScreen: {
    opacity: 0,
  },
});
